import { createDebugger } from '../utils/debug.js';

const debug = createDebugger('product-variant-images', false);

/**
 * Product Variant Images Web Component
 * Handles instant switching between variant images without reload
 * Usage: <product-variant-images></product-variant-images>
 */
class ProductVariantImages extends HTMLElement {
    constructor() {
        super();
        this.currentVariantId = null;
        this.variantChangeHandler = null;
        debug.log('ProductVariantImages web component created');
    }

    connectedCallback() {
        this.imageContainer = this.querySelector('[data-product-variant-images]');

        if (!this.imageContainer) {
            debug.warn('No variant images container found');
            return;
        }

        this.variantImages = this.imageContainer.querySelectorAll('[data-variant-id]');

        debug.log('ProductVariantImages connected', {
            element: this,
            imageContainer: this.imageContainer,
            variantImagesCount: this.variantImages.length
        });

        this.init();
    }

    disconnectedCallback() {
        debug.log('ProductVariantImages disconnected');

        // Remove event listener to prevent memory leaks
        if (this.variantChangeHandler) {
            document.removeEventListener('product:variant-change', this.variantChangeHandler);
            this.variantChangeHandler = null;
        }
    }

    init() {
        // Find currently visible variant
        const currentImage = this.imageContainer.querySelector('.product-variant-image:not(.hidden)');
        if (currentImage) {
            this.currentVariantId = currentImage.dataset.variantId;
            debug.log('Current variant ID found', { currentVariantId: this.currentVariantId });
        }

        // Store handler reference for cleanup
        this.variantChangeHandler = (event) => {
            debug.log('Variant change event received', { event });
            this.handleVariantChange(event.detail.variant);
        };

        // Listen for variant change events
        document.addEventListener('product:variant-change', this.variantChangeHandler);

        debug.log('Event listeners attached');
    }

    handleVariantChange(variant) {
        if (!variant || !variant.id) {
            debug.warn('Invalid variant received', { variant });
            return;
        }

        const newVariantId = variant.id.toString();

        if (newVariantId === this.currentVariantId) {
            debug.log('Same variant selected, no change needed', { variantId: newVariantId });
            return;
        }

        debug.log('Switching to variant', {
            from: this.currentVariantId,
            to: newVariantId
        });

        this.switchToVariant(newVariantId);
    }

    switchToVariant(variantId) {
        // Hide all variant images
        this.variantImages.forEach((imageEl) => {
            imageEl.classList.add('hidden');
            debug.log('Hiding variant image', { variantId: imageEl.dataset.variantId });
        });

        // Show the selected variant image
        const targetImage = this.imageContainer.querySelector(`[data-variant-id="${variantId}"]`);
        if (targetImage) {
            targetImage.classList.remove('hidden');
            this.currentVariantId = variantId;
            debug.log('Showing variant image', { variantId });
        } else {
            debug.error('Variant image not found', { variantId });

            // Fallback: show first available image
            const firstImage = this.variantImages[0];
            if (firstImage) {
                firstImage.classList.remove('hidden');
                this.currentVariantId = firstImage.dataset.variantId;
                debug.log('Fallback to first image', { variantId: this.currentVariantId });
            }
        }
    }
}

// Register the web component
customElements.define('product-variant-images', ProductVariantImages);

export default ProductVariantImages;
